"use client";

import { Link } from "react-router";

import type {
  iBrandDefaultLedgerAccounts,
  iCustomerPaymentMethodDefaultLedgerAccounts,
  iOrganizationDefaultLedgerAccounts,
  iSupplierDefaultLedgerAccounts,
  iSupplierPaymentMethodDefaultLedgerAccounts,
} from "@sdk/default-ledger-accounts/default-ledger-accounts-model";
import { WORKSPACE_DEFAULT_LEDGER_DEFINITIONS } from "@sdk/default-ledger-accounts/default-ledger-accounts-model";
import { loadLedgerById } from "@/store/modules/ledgers/ledgers.helpers";
import { selectLedgerById } from "@/store/modules/ledgers/ledgers.selectors";
import { useQueryWithStore } from "@/store/store.hooks";
import { cn } from "@/lib/utils";

function ledgerTransactionsHref(ledgerId: string): string {
  return `/ledger-transactions?accountId=${encodeURIComponent(ledgerId)}`;
}

function LedgerLinkRow({
  label,
  ledgerId,
  className,
}: {
  label: string;
  ledgerId?: string;
  className?: string;
}) {
  const id = String(ledgerId ?? "").trim();
  const { state: ledger, isLoading } = useQueryWithStore(
    (state) => (id ? selectLedgerById(id)(state) : undefined),
    id
      ? loadLedgerById(id)()
      : (_store: unknown, _forceReload?: boolean) => Promise.resolve(),
    [id],
    !id
  );

  if (!id) {
    return (
      <div className={cn("text-sm", className)}>
        <span className="text-muted-foreground">{label}: </span>
        <span>Not configured</span>
      </div>
    );
  }

  const name = ledger?.name?.trim() || (isLoading ? "Loading…" : id);
  const currency = ledger?.currency ? ` (${ledger.currency})` : "";

  return (
    <div className={cn("text-sm", className)}>
      <span className="text-muted-foreground">{label}: </span>
      <Link
        to={ledgerTransactionsHref(id)}
        className="font-medium text-primary hover:underline"
      >
        {name}
        {currency}
      </Link>
    </div>
  );
}

export function BrandDefaultLedgerLinks({
  defaultLedgerAccounts,
  className,
}: {
  defaultLedgerAccounts?: iBrandDefaultLedgerAccounts;
  className?: string;
}) {
  return (
    <div className={cn("space-y-1", className)}>
      <LedgerLinkRow
        label="Accounts receivable"
        ledgerId={defaultLedgerAccounts?.accountsReceivableLedgerId}
      />
      <LedgerLinkRow
        label="Sales revenue"
        ledgerId={defaultLedgerAccounts?.revenueLedgerId}
      />
      <LedgerLinkRow
        label="VAT liability"
        ledgerId={defaultLedgerAccounts?.vatLiabilityLedgerId}
      />
    </div>
  );
}

export function CustomerPaymentMethodDefaultLedgerLinks({
  defaultLedgerAccounts,
  className,
}: {
  defaultLedgerAccounts?: iCustomerPaymentMethodDefaultLedgerAccounts;
  className?: string;
}) {
  return (
    <div className={cn("space-y-1", className)}>
      <LedgerLinkRow
        label="Receiving account"
        ledgerId={defaultLedgerAccounts?.receivingLedgerId}
      />
      <LedgerLinkRow
        label="PDQ fees account"
        ledgerId={defaultLedgerAccounts?.pdqFeesLedgerId}
      />
      <LedgerLinkRow
        label="Clearing account"
        ledgerId={defaultLedgerAccounts?.clearingLedgerId}
      />
    </div>
  );
}

export function SupplierDefaultLedgerLinks({
  defaultLedgerAccounts,
  className,
}: {
  defaultLedgerAccounts?: iSupplierDefaultLedgerAccounts;
  className?: string;
}) {
  return (
    <div className={cn("space-y-1", className)}>
      <LedgerLinkRow
        label="Expense account"
        ledgerId={defaultLedgerAccounts?.expenseLedgerId}
      />
      <LedgerLinkRow
        label="Accounts payable"
        ledgerId={defaultLedgerAccounts?.accountsPayableLedgerId}
      />
    </div>
  );
}

export function SupplierPaymentMethodDefaultLedgerLinks({
  defaultLedgerAccounts,
  className,
}: {
  defaultLedgerAccounts?: iSupplierPaymentMethodDefaultLedgerAccounts;
  className?: string;
}) {
  return (
    <div className={cn("space-y-1", className)}>
      <LedgerLinkRow
        label="Clearing account"
        ledgerId={defaultLedgerAccounts?.clearingLedgerId}
      />
    </div>
  );
}

export function OrganizationDefaultLedgerLinks({
  defaultLedgerAccounts,
  className,
}: {
  defaultLedgerAccounts?: iOrganizationDefaultLedgerAccounts;
  className?: string;
}) {
  const expenseLedgers = WORKSPACE_DEFAULT_LEDGER_DEFINITIONS.filter(
    (definition) => definition.group === "expense"
  );
  const cashLedgers = WORKSPACE_DEFAULT_LEDGER_DEFINITIONS.filter(
    (definition) => definition.group === "cash"
  );
  const liabilityLedgers = WORKSPACE_DEFAULT_LEDGER_DEFINITIONS.filter(
    (definition) => definition.group === "liability"
  );

  return (
    <div className={cn("space-y-3", className)}>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Trade expense accounts
        </p>
        {expenseLedgers.map((definition) => (
          <LedgerLinkRow
            key={definition.key}
            label={definition.label}
            ledgerId={defaultLedgerAccounts?.[definition.key]}
          />
        ))}
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Cash & clearing accounts
        </p>
        {cashLedgers.map((definition) => (
          <LedgerLinkRow
            key={definition.key}
            label={definition.label}
            ledgerId={defaultLedgerAccounts?.[definition.key]}
          />
        ))}
      </div>
      {liabilityLedgers.length > 0 ? (
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Customer credit accounts
          </p>
          {liabilityLedgers.map((definition) => (
            <LedgerLinkRow
              key={definition.key}
              label={definition.label}
              ledgerId={defaultLedgerAccounts?.[definition.key]}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function hasBrandDefaultLedgers(
  defaultLedgerAccounts?: iBrandDefaultLedgerAccounts
): boolean {
  return Boolean(
    defaultLedgerAccounts?.accountsReceivableLedgerId?.trim() &&
      defaultLedgerAccounts?.revenueLedgerId?.trim()
  );
}
